import { useContext, useState } from "react";
import { Settings } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/modern/components/ui/alert-dialog";
import BryntumContext from "@/react/context/bryntumContext.jsx";
import { GRANTS } from "@/other/constants/PrivilegeTypes.jsx";
import { VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";
import { SETTING_KEYS } from "@/react/components/entityViews/settings/constants/settingsStructure.js";
import { cn } from "@/modern/utils";

/** Above File Detail (+10) and Spot Incentive pickers (+20). */
const SETTINGS_CONFIRM_Z = "z-[calc(var(--swivu-bryntum-z-index)+30)]";

function openIncentiveCategoriesSettings(viewManager: {
  openAnonymousViewByType: (opts: {
    viewType: string;
    viewName: string;
    otherParams: { setting: string };
  }) => void;
}) {
  viewManager.openAnonymousViewByType({
    viewType: VIEW_TYPES.settings,
    viewName: "Settings",
    otherParams: { setting: SETTING_KEYS.INCENTIVE_CATEGORIES_AND_TAGS },
  });
}

type IncentiveSettingsGearButtonProps = {
  className?: string;
};

/**
 * Gear that opens Incentive Categories & Tags. Confirms first because that
 * navigation closes Adjust Points and drops unsaved tag / form edits.
 */
export function IncentiveSettingsGearButton({ className }: IncentiveSettingsGearButtonProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { user, viewManager } = useContext(BryntumContext) as any;
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!user?.can?.(GRANTS.grant_one)) return null;

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className={cn("size-9 shrink-0 rounded-full border-input bg-muted", className)}
            aria-label="Open Categories & Tags settings"
            onClick={() => setConfirmOpen(true)}
          >
            <Settings className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className={SETTINGS_CONFIRM_Z}>
          Open Categories & Tags settings
        </TooltipContent>
      </Tooltip>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent
          overlayClassName={SETTINGS_CONFIRM_Z}
          className={cn(SETTINGS_CONFIRM_Z, "rounded-[2rem] sm:rounded-[2rem]")}
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-normal tracking-tight">Open settings?</AlertDialogTitle>
            <AlertDialogDescription>
              Adjust Points will close. Unsaved changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full font-normal">Close</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-full font-normal"
              onClick={() => openIncentiveCategoriesSettings(viewManager)}
            >
              Open settings
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
